import { Link } from 'react-router-dom';

import { SiteNav } from "../components/SiteNav";
import SiteFooter from '../components/SiteFooter';

const H2 = ({ children, id }) => (
  <h2 id={id} className="font-heading text-xl font-medium mt-10 mb-4 scroll-mt-24" style={{ color: '#2A1E5C' }}>{children}</h2>
);
const P = ({ children }) => (
  <p className="font-body text-base leading-relaxed mb-4" style={{ color: '#5D5A6B' }}>{children}</p>
);
const LI = ({ children }) => (
  <li className="font-body text-base leading-relaxed">{children}</li>
);

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteNav />

      {/* Content */}
      <section className="pt-12 pb-16 sm:pt-16 lg:pt-20 lg:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto" data-testid="privacy-content">
          <h1 className="font-heading text-3xl sm:text-4xl font-medium mb-4" style={{ color: '#2A1E5C' }}>
            Payday Relay Privacy Policy
          </h1>
          <p className="font-body text-sm mb-8" style={{ color: '#8B7E6A' }} data-testid="privacy-last-updated">
            Last updated: October 2, 2026
          </p>

          <div className="prose prose-lg" style={{ color: '#5D5A6B' }}>
            <P>
              This Privacy Policy explains how Earthwise Digital LLC collects, uses, shares, and protects
              information when you use Payday Relay, the Payday Relay app, the Relay Calculator, related worksheets,
              emails, Google Sheets integrations, and beta features.
            </P>

            <H2>1. Information We Collect</H2>
            <P>We may collect:</P>
            <ul className="list-disc pl-6 mb-6 space-y-2" style={{ color: '#5D5A6B' }}>
              <LI>Contact information, such as your name and email address</LI>
              <LI>Signup, beta, purchase, and subscription information</LI>
              <LI>Relay Calculator responses, such as income goals, debt payoff goals, savings goals, work days, weeks off, tax set-aside preferences, and payday rhythm</LI>
              <LI>Payday Relay entries, such as earnings logs, dates, weekly progress, PTO set-aside amounts, and related planning information</LI>
              <LI>Google connection information, such as authorization status, connected sheet IDs, and the data needed to create, read, or update your Payday Relay sheet</LI>
              <LI>Support messages, feedback, survey responses, testimonials, and bug reports</LI>
              <LI>Basic usage information, such as pages visited, device/browser type, and feature interactions</LI>
              <LI>Payment status and transaction metadata from payment processors such as Stripe</LI>
            </ul>
            <P><strong style={{ color: '#2A1E5C' }}>We do not collect or store your full payment card number.</strong></P>

            <H2>2. How We Use Information</H2>
            <P>We use your information to:</P>
            <ul className="list-disc pl-6 mb-6 space-y-2" style={{ color: '#5D5A6B' }}>
              <LI>Create and manage your Payday Relay account</LI>
              <LI>Generate Relay Calculator results</LI>
              <LI>Build, populate, update, and support your Payday Relay Google Sheet</LI>
              <LI>Let you log earnings and track progress in the Payday Relay app</LI>
              <LI>Send setup links, magic links, product emails, receipts, support replies, and beta updates</LI>
              <LI>Improve product design, calculations, onboarding, and user experience</LI>
              <LI>Diagnose bugs, prevent misuse, and protect the service</LI>
              <LI>Manage purchases, subscriptions, refunds, and customer support</LI>
              <LI>Comply with legal obligations</LI>
            </ul>

            <H2 id="google-data">3. Google User Data, Google Sheets, and OAuth</H2>
            <P>
              Payday Relay connects to your Google account through Google&rsquo;s OAuth sign-in. This section
              explains exactly what we ask for, why, and what we do with it.
            </P>
            <p className="font-body text-base font-semibold mb-2" style={{ color: '#2A1E5C' }}>What Google data we access</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" style={{ color: '#5D5A6B' }} data-testid="privacy-google-scopes">
              <LI><strong>Your Google account email address</strong> (<code>userinfo.email</code> and <code>openid</code>), so we can show you which account your sheet lives in and offer a reconnect if access expires.</LI>
              <LI><strong>Per-file Google Drive access</strong> (<code>drive.file</code>), which lets Payday Relay create your spreadsheet in your own Google Drive and then read and update <strong>only that one file</strong>. This scope gives us no visibility into any other file in your Drive &mdash; just the Payday Relay sheet we create for you (or a file you explicitly open with Payday Relay).</LI>
            </ul>
            <P>
              We do not request access to your full Google Drive, Gmail, Calendar, Contacts, or any other
              Google service. The <code>drive.file</code> scope is intentionally narrow: Payday Relay can see and
              change only the single Payday Relay spreadsheet it creates, and never any other file in your Drive
              (we remember only that file&rsquo;s ID). We cannot list, read, or modify anything else.
            </P>
            <p className="font-body text-base font-semibold mb-2" style={{ color: '#2A1E5C' }}>Why we need per-file access</p>
            <P>
              Your Payday Relay Google Sheet is the permanent, portable record of your race. It lives in your
              Drive, not on our servers. This per-file access is what lets the app build that sheet from our
              template, pre-load it with your Relay Calculator numbers, write what you log in the app into the
              right cells, and read your relay calendar and targets back so the app and the sheet always agree.
            </P>
            <p className="font-body text-base font-semibold mb-2" style={{ color: '#2A1E5C' }}>How sheet data is used, stored, and synced</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" style={{ color: '#5D5A6B' }}>
              <LI><strong>Writes:</strong> daily earnings you log, relay close-out amounts (taxes, PTO, savings, debt), PTO days you draw from your PTO bank, and quarterly target changes are written to your sheet when you take those actions in the app.</LI>
              <LI><strong>Reads:</strong> we read your relay calendar, weekly targets, logged earnings, and the PTO/bonus summary cells to render your dashboard and Scoreboard, and to check that the sheet&rsquo;s formulas are intact.</LI>
              <LI><strong>Storage:</strong> we store your Google OAuth tokens encrypted at rest, your spreadsheet ID, and a copy of the entries you log in the app (so the app works even when Google is briefly unreachable). We do not store a full copy of your spreadsheet.</LI>
              <LI><strong>Sync direction:</strong> the app writes to the sheet; edits you make directly in the sheet are read back the next time the app refreshes. If the connection expires, the app tells you and offers a one-tap reconnect. Nothing is lost in the meantime.</LI>
            </ul>
            <P>
              <strong style={{ color: '#2A1E5C' }}>Google data is used only to provide Payday Relay&rsquo;s user-facing
              features to you.</strong> We do not use it for advertising, we do not sell it, we do not share it
              with data brokers, and we do not use your Google user data to train AI or machine-learning models.
              Humans at Earthwise Digital only look at your sheet data with your permission to help you with a
              support request, or where required for security or by law.
            </P>
            <P>
              You can disconnect Google at any time from the app or through your{' '}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" style={{ color: '#2A1E5C', textDecoration: 'underline' }}>
                Google account permissions
              </a>. When you do, we delete the stored tokens. Your spreadsheet stays in your Drive. It is
              yours, and the app simply stops filling it in.
            </P>
            <P>
              Payday Relay&rsquo;s use and transfer of information received from Google APIs to any other app
              adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer" style={{ color: '#2A1E5C', textDecoration: 'underline' }}>
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </P>

            <H2>4. How We Share Information (Service Providers)</H2>
            <P>
              We do not sell your personal information. Not to advertisers, not to data brokers, not to
              anyone. We share information only with the service providers that help us run Payday Relay, and only
              the minimum each one needs:
            </P>
            <ul className="list-disc pl-6 mb-6 space-y-2" style={{ color: '#5D5A6B' }} data-testid="privacy-vendors">
              <LI><strong>Google</strong> (Sheets and OAuth APIs), to build and sync your spreadsheet, as described above.</LI>
              <LI><strong>Stripe</strong>, payment processing, subscriptions, and the billing portal. Stripe holds your card details; we never see the full number.</LI>
              <LI><strong>Resend</strong>, delivers our transactional emails (setup links, sign-in links, reminders, receipts).</LI>
              <LI><strong>Hosting, database, and application platform providers</strong>, run the app and store your account data. Our database is hosted on managed infrastructure with encryption at rest.</LI>
              <LI><strong>AI model providers</strong>, when you chat with Caddy or ask for a coaching message, the text of your message is sent to a large-language-model provider to generate a reply. If you switch on <em>&ldquo;Let Caddy use my relay progress&rdquo;</em> in Settings (it is off by default), Caddy is also given a small summary of your current relay &mdash; your weekly target, what you have earned and received, days left, and whether a payment is past due. <strong>These figures are calculated by Payday Relay from what you log in the app; your Google Sheet, Google credentials, and raw entry history are never read for this and never sent.</strong> The app does not attach your name or email to what it sends, though, as with any chat, anything you type yourself (including a name or email you choose to enter) is part of your message and is sent so Caddy can read it. These providers are contractually restricted from using your data to train their models.</LI>
              <LI><strong>Web push</strong>, if you turn on notifications, your browser&rsquo;s push service (Apple, Google, or Mozilla) delivers the reminder; the message content is generic and contains no dollar amounts.</LI>
            </ul>
            <P>
              These providers act on our instructions and are bound by their own privacy and security commitments.
              We may also share information if required by law, to protect our rights, to prevent fraud or abuse,
              or as part of a business transfer such as a merger or acquisition, in which case this policy
              continues to apply to your data.
            </P>

            <H2>Artificial Intelligence (Caddy)</H2>
            <P>
              Payday Relay includes an AI assistant named Caddy. Caddy drafts coaching messages and answers
              general questions about running an independent income, and &mdash; only if you turn on
              &ldquo;Let Caddy use my relay progress&rdquo; in Settings &mdash; can answer questions about your week
              using figures the app calculates from what you&rsquo;ve logged. Caddy never reads your Google Sheet.
              Caddy is a coach, not a professional. Caddy&rsquo;s replies are generated text, can be wrong, and are
              never financial, tax, or legal advice. Caddy reports what you have <em>earned</em> separately from what
              you have actually <em>received</em>, and never treats a pending payment, or money set aside for tax or
              savings, as though it has already arrived.
              We keep a record of your Caddy conversations, stored with your account, so we can improve the
              product, keep it safe, and so you can send a conversation to our support team (support@paydayrelay.com)
              when you want a human to help. We do not use these conversations to build profiles of you or sell to anyone.
            </P>

            <H2>5. Emails</H2>
            <P>
              We may send transactional emails, including setup links, login links, receipts, account notices,
              beta updates, and support replies.
            </P>
            <P>
              If you join a mailing list or receive marketing emails, you can unsubscribe from marketing
              emails. Transactional emails may still be sent when needed to provide the service.
            </P>
            <P>
              Every non-billing email carries a private preferences link, so you can choose exactly what we
              send you, progress updates, product news, general news, or beta updates, or switch all of it
              off, without signing in. You can do the same from Settings inside the app. Billing and account
              notices are not optional: they are the receipts for money that moves.
            </P>

            <H2 id="research">Research (optional, off unless you turn it on)</H2>
            <P>
              If you choose to take part, Payday Relay uses de-identified, aggregated data to understand
              whether a weekly finish line helps independent earners build steadier income, set money aside,
              and take real time off.
            </P>
            <P>
              Taking part is entirely optional and always your choice. We ask once during onboarding, with
              nothing pre-selected and a &ldquo;Not now&rdquo; option that is just as easy to choose as
              &ldquo;Yes.&rdquo; You can also turn it on or off any time in Settings. Your decision is separate
              from your beta access: declining, or turning it off later, changes nothing about your account,
              the app, or any feature you can use.
            </P>
            <P>
              When research contribution is on, we store a de-identified record of your activity from that
              point forward. Each record is linked to a salted, one-way identifier derived from your email
              rather than to your name or email address. These records capture patterns such as the relays you
              clear, time off you take, and how pending (earned-but-not-yet-received) income resolves. Money is
              stored only as a range (for example $500&ndash;1,000) and progress only as a band (for example
              50&ndash;75% of goal); we do not store your exact amounts or dates in these records. Turning
              research on does not include any activity from before you opted in.
            </P>
            <P>
              These records are <strong style={{ color: '#2A1E5C' }}>de-identified, not anonymous</strong>:
              because the identifier is derived from your email, we retain the technical ability to re-link a
              record to your account in limited circumstances &mdash; for example, to honor a privacy request,
              or where required for security or by law. We do not do this as part of ordinary research, and any
              such lookup is logged internally. We report findings only as percentages, counts, medians, and
              patterns across groups, and we withhold any group too small to report safely.
            </P>
            <P>
              We keep a private record of your consent choice and the version of this explanation you agreed
              to, so we can honor it. You can turn research contribution off at any time; when you do, we stop
              including your activity in any future research. Aggregate findings already produced before you
              opted out may remain, since they are group-level and not traceable to you individually.
            </P>

            <H2>6. Data Retention</H2>
            <P>
              We keep information as long as needed to provide the product, support your account, comply with
              legal obligations, resolve disputes, and improve the service.
            </P>
            <P>You may request deletion of your account or certain information by contacting us.</P>

            <H2>7. Security</H2>
            <P>
              We use reasonable safeguards to protect personal information. No online service is perfectly
              secure, and we cannot guarantee absolute security.
            </P>
            <P>
              You are responsible for protecting access to your email account, Google account, and any
              connected services.
            </P>

            <H2>8. Your Choices</H2>
            <P>You may:</P>
            <ul className="list-disc pl-6 mb-6 space-y-2" style={{ color: '#5D5A6B' }}>
              <LI>Request access to information associated with your account</LI>
              <LI>Ask us to correct or delete certain information</LI>
              <LI>Unsubscribe from marketing emails</LI>
              <LI>Revoke Google access through Google account settings</LI>
              <LI>Cancel subscriptions through the billing portal, if available</LI>
            </ul>
            <P>
              To make a privacy request, contact:{' '}
              <a href="mailto:support@paydayrelay.com" style={{ color: '#2A1E5C', textDecoration: 'underline' }} data-testid="privacy-contact-email">
                support@paydayrelay.com
              </a>
            </P>

            <H2>9. Children</H2>
            <P>Payday Relay is intended for adults and is not directed to children under 13.</P>

            <H2>10. Changes to This Policy</H2>
            <P>
              We may update this Privacy Policy as Payday Relay changes. The updated version will be posted
              with a new &ldquo;Last updated&rdquo; date.
            </P>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
